import { clear, del } from 'idb-keyval'
import { objectEntries } from 'tsafe/objectEntries'
import { objectFromEntries } from 'tsafe/objectFromEntries'
import { objectKeys } from 'tsafe/objectKeys'

import type { RouteLocationRaw } from 'vue-router'

import { AUTOMATED_TESTING_CONFIG_COOKIES_KEY } from '@ga/constants/automated-testing'
import { EventBus } from '@ga/utils'

import { COUNTRY, COUNTRY_GROUP, LANGUAGE } from '../constant'
import { routes } from '../router'

import type {
  IBaseAppModule,
  TAppModulesRepositories,
  TAppModulesServices,
  TAppModulesStores,
  TBaseAppModuleRepositories,
  TBaseAppModuleServices,
  TBaseAppModuleStores,
  TModuleLayerKeys,
} from './types'
import { locationHandler, mergeLocales } from './utils'

import type { NuxtApp } from '#app'
import { Config } from '~/config'
import { getCountryFromLocale } from '~/locales/utils'

type TLocaleMissingProps = { country?: string } & ComputedRef<{
  country?: string
}>

export class GaApp {
  nuxtApp: NuxtApp
  appVue: NuxtApp['vueApp']
  config: Config<any>
  eventBus: EventBus
  routes: typeof routes
  router: NuxtApp['$router']
  i18n:
    | {
        // NUXT3: фактически country есть в localeProperties, но почему-то не объявлен в LocaleObject модуля @nuxt/i18n
        locale: NuxtApp['$i18n']['localeProperties'] & TLocaleMissingProps
        locales: NuxtApp['$i18n']['locales']
        t: NuxtApp['$i18n']['t']
        te: NuxtApp['$i18n']['te']
        tm: NuxtApp['$i18n']['tm']
        setLocaleCookie: NuxtApp['$i18n']['setLocaleCookie']
        switchLocalePath: NuxtApp['$switchLocalePath']
      }
    | Record<string, never>

  modules: {
    services: TAppModulesServices
    stores: TAppModulesStores
    repositories: TAppModulesRepositories
    locales: Record<string, any>
  }

  isCountry: {
    [K in keyof typeof COUNTRY as Lowercase<K>]: boolean
  } & {
    group: {
      [K in keyof typeof COUNTRY_GROUP as Lowercase<K>]: boolean
    }
  }

  isLanguage: {
    [K in keyof typeof LANGUAGE as Lowercase<K>]: boolean
  }

  constructor(nuxtApp: NuxtApp, publicRuntimeConfig: any) {
    this.nuxtApp = nuxtApp

    this.appVue = nuxtApp.vueApp
    this.config = new Config(publicRuntimeConfig)
    this.eventBus = new EventBus()
    this.routes = routes
    this.router = this.getRouter()

    this.i18n = {}

    this.modules = {
      services: {} as TAppModulesServices,
      locales: {},
      stores: {} as TAppModulesStores,
      repositories: {} as TAppModulesRepositories,
    }

    this.isCountry = this.getIsCountry()
    this.isLanguage = this.getIsLanguage()

    // узнаем про новые релизы приложения
    this.nuxtApp.hook('app:manifest:update', () => {
      this.stores.app.main.hasForceUpdate = true
    })
  }

  get configNuxt() {
    return this.nuxtApp.$config
  }

  get services() {
    return this.modules.services
  }

  get stores() {
    return this.modules.stores
  }

  get repositories() {
    return this.modules.repositories
  }

  get isClient() {
    return Boolean(import.meta.client)
  }

  get isServer() {
    return Boolean(!import.meta.client)
  }

  get isWebview() {
    return Boolean(this.stores.app.main.webview)
  }

  get redirectError() {
    return showError
  }

  get redirect() {
    const {
      $localePath: localePath,
      $i18n: { locale, defaultLocale },
    } = this.nuxtApp
    return async (location: any) =>
      navigateTo(
        locationHandler(location, {
          locale,
          defaultLocale,
          localePath,
        }) as RouteLocationRaw,
      )
  }

  get serverRequest() {
    return this.nuxtApp.ssrContext?.event.node.req
  }

  get route() {
    return this.nuxtApp._route
  }

  get store() {
    return this.nuxtApp.store
  }

  get cookies() {
    return this.nuxtApp.$cookies
  }

  get automatedTestingConfig() {
    return this.cookies.get(AUTOMATED_TESTING_CONFIG_COOKIES_KEY)
  }

  get isRtl() {
    return this.i18n?.locale.value.dir === 'rtl'
  }

  get isMiddleEast() {
    const country = this.i18n.locale.value.country
    return country !== undefined && COUNTRY_GROUP.ME.includes(country)
  }

  get deviceId() {
    return this.nuxtApp.$deviceId
  }

  /** Предоставляет методы управления кешем indexDB, генераци ключа и удаление */
  get cache() {
    return {
      genId: (name: string) => `${name}-${this.i18n?.locale.value.code}`,
      del: async (name: string) =>
        del(`${name}-${this.i18n?.locale.value.code}`),
      clear: async () => clear(),
    }
  }

  /**
   * Предоставляет флаги чтобы проверить какая страна или группа стран сейчас выбрана
   */
  getIsCountry() {
    const locale = this.config.get('locale').active as string

    const currentCountry = getCountryFromLocale(locale) as string

    const countryBooleanFlags = objectFromEntries(
      objectEntries(COUNTRY).map(([key, value]) => [
        key.toLowerCase(),
        value === currentCountry,
      ]),
    ) as {
      [K in keyof typeof COUNTRY as Lowercase<K>]: boolean
    }

    const countryGroupBooleanFlags = objectFromEntries(
      objectEntries(COUNTRY_GROUP).map(([key, value]) => [
        key.toLowerCase(),
        value.includes(currentCountry),
      ]),
    ) as {
      [K in keyof typeof COUNTRY_GROUP as Lowercase<K>]: boolean
    }

    return {
      ...countryBooleanFlags,
      group: {
        ...countryGroupBooleanFlags,
      },
    }
  }

  getIsLanguage() {
    const currentLanguage = this.nuxtApp.$i18n.locale.value

    const languageBooleanFlags = objectFromEntries(
      objectEntries(LANGUAGE).map(([key, value]) => {
        return [key.toLowerCase(), value === currentLanguage]
      }),
    ) as {
      [K in keyof typeof LANGUAGE as Lowercase<K>]: boolean
    }

    return languageBooleanFlags
  }

  get analytics() {
    return this.nuxtApp.$analytics
  }

  get api() {
    return this.nuxtApp.$api
  }

  get mq() {
    return this.nuxtApp.$mq
  }

  get isInit() {
    return Boolean(this.stores.app.main.init)
  }

  get libs() {
    return {
      // eslint-disable-next-line ts/no-unsafe-assignment
      apm: this.nuxtApp.$apm,
      dateWrapper: this.nuxtApp.$dateWrapper,
      notifier: this.nuxtApp.$notifier,
      // eslint-disable-next-line ts/no-unsafe-assignment
      scrollTo: this.nuxtApp.$scrollTo,
    }
  }

  get getDataTestId() {
    return this.nuxtApp.$getDataTestId
  }

  getRouter() {
    return new Proxy(this.nuxtApp.$router, {
      get: (target, prop) => {
        if (prop === 'push') {
          const {
            $localePath: localePath,
            $i18n: { locale, defaultLocale },
          } = this.nuxtApp

          return async (
            location: any,
            onComplete: (...args: any[]) => any,
            onAbort: (...args: any[]) => any,
          ) =>
            // onComplete, onAbort - любые функции, разрешаем возвращать any
            // eslint-disable-next-line ts/no-unsafe-return
            target
              .push(
                locationHandler(location, {
                  locale,
                  defaultLocale,
                  localePath,
                }) as RouteLocationRaw,
              )
              .then(onComplete, onAbort)
        }

        return target[prop as keyof typeof target]
      },
    })
  }

  installModules<T extends readonly IBaseAppModule[]>(modules: T): void {
    modules.forEach((module) => {
      const { meta, components, layers } = module

      const moduleName = meta.name

      if (meta.routes) {
        meta.routes.forEach((route: any) => {
          // eslint-disable-next-line ts/no-unsafe-argument
          this.routes.push(route)
        })
      }

      // если в локалях модуля есть дефолтная локаль (ru-RU)
      if (meta.locales?.[this.config.get('locale').default] !== undefined) {
        const moduleLocales = meta.locales
        // eslint-disable-next-line ts/no-unsafe-assignment
        const defaultLocale = moduleLocales[this.config.get('locale').default]

        // берем список доступных локалей
        const locales = this.nuxtApp.$i18n.locales.value.map(({ iso }) => iso)

        locales.forEach((locale) => {
          if (locale === undefined) return

          if (!(locale in this.modules.locales)) {
            this.modules.locales[locale] = {}
          }
          // если какой-либо локали нет в модуле, будет добавлено содержимое дефолтной локали
          // eslint-disable-next-line ts/no-unsafe-assignment, ts/no-unsafe-member-access
          this.modules.locales[locale][moduleName] = mergeLocales(
            defaultLocale,
            moduleLocales[locale],
          )
        })
      }

      if (components) {
        objectKeys(components).forEach((name) => {
          this.appVue.component(String(name), components[name])
        })
      }

      if (layers) {
        this.installModuleLayers(moduleName, layers)
      }
    })
  }

  private installModuleLayers(
    moduleName: IBaseAppModule['meta']['name'],
    layers: NonNullable<IBaseAppModule['layers']>,
  ): void {
    objectKeys(layers).forEach((layer) => {
      const layerKey = layer as TModuleLayerKeys

      if (layers[layerKey] === undefined) return

      if (layerKey === 'analytics') {
        const analyticsModules = this.analytics.modules as Record<string, any>
        analyticsModules[moduleName] = layers[layerKey](
          this,
          this.analytics.libs,
        )
      } else {
        // Типобезопасное присвоение для каждого слоя
        this.assignModuleLayer(layerKey, moduleName, layers[layerKey])
      }
    })
  }

  private assignModuleLayer(
    layerKey: Exclude<TModuleLayerKeys, 'analytics'>,
    moduleName: IBaseAppModule['meta']['name'],
    layerFactory:
      | TBaseAppModuleStores
      | TBaseAppModuleServices
      | TBaseAppModuleRepositories,
  ): void {
    const moduleLayer = this.modules[layerKey as keyof typeof this.modules]
    moduleLayer[moduleName] = layerFactory(this)
  }
}
